import { useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import type { StepProps } from '../../../types/enrollment.types';

export interface SignatureStepRef {
  getSignatureData: () => string;
  isEmpty: () => boolean;
  clear: () => void;
  isSkipped: () => boolean;
}

interface SignatureStepProps extends StepProps {
  onSignatureChange?: (data: string) => void;
  onSkipSignatureChange?: (skip: boolean) => void;
}

const SignatureStep = forwardRef<SignatureStepRef, SignatureStepProps>(
  ({ formData, errors, onSignatureChange, onSkipSignatureChange }, ref) => {
    const { t } = useTranslation();
    const signatureRef = useRef<SignatureCanvas>(null);

    useImperativeHandle(ref, () => ({
      getSignatureData: () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
          return signatureRef.current.toDataURL();
        }
        return '';
      },
      isEmpty: () => {
        return !signatureRef.current || signatureRef.current.isEmpty();
      },
      clear: () => {
        signatureRef.current?.clear();
        onSignatureChange?.('');
      },
      isSkipped: () => {
        return formData.skipSignature === true;
      },
    }));

    const handleClear = () => {
      signatureRef.current?.clear();
      onSignatureChange?.('');
    };

    const handleEnd = () => {
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        onSignatureChange?.(signatureRef.current.toDataURL());
      }
    };

    const handleSkipChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const checked = event.target.checked;
      onSkipSignatureChange?.(checked);
      // Clear any existing signature when toggling skip
      if (checked && signatureRef.current) {
        signatureRef.current.clear();
        onSignatureChange?.('');
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Typography variant="h5" gutterBottom>
          {t('step.signature')}
        </Typography>

        <Typography variant="body1" color="text.secondary">
          {t('signature.instruction')}
        </Typography>

        {/* Staff enrollment checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.skipSignature}
              onChange={handleSkipChange}
              color="primary"
            />
          }
          label={t('signature.skipForStaff')}
          sx={{
            backgroundColor: 'action.hover',
            borderRadius: 1,
            py: 1,
            px: 2,
            mr: 0,
          }}
        />

        {formData.skipSignature ? (
          /* Info message when skipping signature */
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('signature.skipInfo')}
          </Alert>
        ) : (
          /* Signature canvas when not skipping */
          <>
            <Paper
              elevation={3}
              sx={{
                border: errors.signatureData ? '2px solid red' : '2px solid #ccc',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <SignatureCanvas
                ref={signatureRef}
                onEnd={handleEnd}
                canvasProps={{
                  width: 700,
                  height: 300,
                  style: { width: '100%', height: 'auto' },
                }}
                backgroundColor="#fff"
              />
            </Paper>

            {errors.signatureData && (
              <Typography color="error">{errors.signatureData}</Typography>
            )}

            <Button variant="outlined" onClick={handleClear} size="large">
              {t('signature.clear')}
            </Button>
          </>
        )}
      </Box>
    );
  }
);

SignatureStep.displayName = 'SignatureStep';

export default SignatureStep;
